import React, { useState, useEffect, useContext } from 'react';

import { WishlistActionContext } from 'src/lib/WishlistActionContext';
import { UserItemContext } from 'src/lib/UserItemContext';

import { OverlayContainer } from '@react-aria/overlays';
import { AddToWishlist } from './AddToWishlist';
import { ShowWishlist } from './ShowWishlist';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { IWishlistItem } from 'src/components/Wishlists/Wishlist.i';

interface IWishlistModal {
  item: IAuctionItem;
  closeModal(newListId?: number): void;
  auctionTitle: string;
  setAddedToList(): void;
  currentList?: number;
  showWishlist?: boolean;
  addToNewWishlist?: boolean;
}

export const WishlistModal = (props: IWishlistModal):JSX.Element => {
  const { auctionWishlists, setAuctionWishlists } = useContext(UserItemContext);
  const { addWishlistItem, createNewWishlist, getWishlistItems } = useContext(WishlistActionContext);

  const [currentListId, setCurrentListId] = useState(props.currentList);
  const [itemList, setItemList] = useState<IWishlistItem[]>([]);
  const [newListName, setNewListName] = useState('');
  const [newItemNote, setNewItemNote] = useState('');
  const [error, setError] = useState('');
  const [selectedList, setSelectedList] = useState('0');
  const [addNew, setAddNew] = useState(false);

  useEffect(():void => {
    const fetchListItems = async ():Promise<void> => {
      const wlItems = await getWishlistItems(currentListId);
      setItemList(wlItems);
    };
    if(props.showWishlist && currentListId) fetchListItems().then();
  }, [currentListId]);

  const saveNewWishlist = async (newListName:string, newItemNote:string):Promise<number> => {
    const newListId = await createNewWishlist(
      newListName,
      props.item.AuctionID,
      props.item.AuctionItemID,
      newItemNote
    );
    setAuctionWishlists([
      {
        WishListID: newListId,
        WishListName: newListName,
      },
      ...auctionWishlists,
    ]);
    return newListId;
  };

  const saveItem = async ():Promise<void> => {
    let listId:string|number;
    if(addNew) {
      listId = await submitNewWishlist();
    } else {
      listId = parseInt(selectedList);
      await addWishlistItem(listId, props.item.AuctionItemID, newItemNote);
    }
    if(listId) {
      props.setAddedToList();
      props.closeModal(listId);
    }
  };

  const submitNewWishlist = async ():Promise<number> => {
    if(auctionWishlists.length > 64) {
      setError('You have too many wishlists. You can delete old lists in your account page');
      return;
    }
    if(newListName.trim().length === 0) {
      setError('Please Enter a Wishlist Name');
      return;
    }
    if(
      auctionWishlists.find(
        (list) => list.WishListName.toLowerCase() === newListName.trim().toLowerCase()
      )
    ) {
      setError(`You have already used the wishlist name "${newListName}"`);
      return;
    }
    return await saveNewWishlist(newListName, newItemNote);
  };

  const wishlistSelect = async (listID:string):Promise<void> => {
    setSelectedList(listID);
    if(listID === '0') {
      return;
    }
    if(listID === '-1') {
      // create new wishlist and add item to it
      setAddNew(true);
    } else {
      if(addNew !== false) setAddNew(false);
    }
  };

  let ModalBodyToRender:JSX.Element;

  if(props.showWishlist)
  {
    ModalBodyToRender = <ShowWishlist
      currentListId={currentListId || selectedList}
      auctionWishlists={auctionWishlists}
      setCurrentListId={setCurrentListId}
      itemList={itemList}
      setItemList={setItemList}
      closeModal={props.closeModal}
      auctionTitle={props.auctionTitle}
    />;
  } else
  {
    ModalBodyToRender = 
      <AddToWishlist
        item={props.item}
        selectedList={selectedList}
        wishlistSelect={wishlistSelect}
        addNew={addNew}
        saveItem={saveItem}
        newItemNote={newItemNote}
        setNewItemNote={setNewItemNote}
        newListName={newListName}
        setNewListName={setNewListName}
        error={error}
        closeModal={props.closeModal}
        auctionTitle={props.auctionTitle}
      />
  }

  return (
    <OverlayContainer>
      {ModalBodyToRender}
    </OverlayContainer>
  );
};
